import { Button, DisplayInfo, Icon, Table, addToast } from '@octano/global-ui';
import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { SectionTitle } from '../../../../../../components/text';
import {
  CurriculumAdmissionRequirementFormValue,
  CurriculumRequirementOption,
} from '../../../../../../types/CurriculumTypes';
import { getStudyPlanFormData } from '../../../api/study-plan';
import { AddAdmissionRequirementModal } from './AddAdmissionRequirementModal';

type CurriculumAdmissionRequirementsProps = {
  admissionRequirements?: CurriculumAdmissionRequirementFormValue[];
  readOnly: boolean;
  setDisabledEditButton: (value: boolean) => void;
};

export const CurriculumAdmissionRequirements = ({
  admissionRequirements,
  readOnly,
  setDisabledEditButton,
}: CurriculumAdmissionRequirementsProps) => {
  const { t } = useTranslation();
  const prefix = 'studyPlans.curriculum.admissionRequirements';
  const [showAddModal, setShowAddModal] = useState(false);
  const [
    curriculumRequirementOptions,
    setCurriculumRequirementOptions,
  ] = useState<CurriculumRequirementOption[]>([]);

  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'admissionRequirements',
    keyName: 'fieldId',
  });

  // RHF 7.10 no expone `replace`, así que hidratamos con `append` (mismo
  // patrón que DegreeDocuments): el componente se remonta por cada malla.
  useEffect(() => {
    if (admissionRequirements?.length) {
      append(admissionRequirements);
    }
  }, [admissionRequirements, append]);

  useEffect(() => {
    const handleGetFormData = async () => {
      const { data, error } = await getStudyPlanFormData();

      if (error) {
        addToast({
          text: t(`${prefix}.errorOnSearch`),
          color: 'danger',
          icon: 'error',
        });
        return;
      }

      setCurriculumRequirementOptions(data?.curriculumRequirements || []);
    };

    handleGetFormData();
  }, [t]);

  const requirementRows = useMemo(() => {
    return fields as (CurriculumAdmissionRequirementFormValue & {
      fieldId: string;
    })[];
  }, [fields]);

  const pendingOptions = useMemo(() => {
    const selectedIds = requirementRows
      .map((row) => row.id)
      .filter((value): value is number => !!value);

    return curriculumRequirementOptions.filter(
      (option) => !selectedIds.includes(option.id),
    );
  }, [curriculumRequirementOptions, requirementRows]);

  return (
    <Row className="pb-5">
      <Col xs={12}>
        <Row>
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.title`)} />
          </Col>
        </Row>
        <Row className="pb-3">
          <Col xs={12}>
            <span className="fs-18">{t(`${prefix}.subtitle`)}</span>
          </Col>
        </Row>
      </Col>
      {!readOnly && (
        <Col xs={12}>
          <Row className="d-flex justify-content-end mb-3">
            <Col xs={12} md={4}>
              <Button
                className="btn-block"
                size="md"
                type="button"
                text={t(`${prefix}.addRequirement`)}
                icon="plus"
                onClick={() => setShowAddModal(true)}
              />
            </Col>
          </Row>
        </Col>
      )}
      <Col xs={12}>
        <Table
          columns={[
            {
              columnName: 'name',
              thClassName: 'text-center',
              tdClassName: 'text-center',
              headerText: t(`${prefix}.table.headers.name`),
              cellFormat({ row }) {
                return row.name;
              },
            },
            {
              columnName: 'description',
              thClassName: 'text-center',
              tdClassName: 'text-center',
              headerText: t(`${prefix}.table.headers.description`),
              cellFormat({ row }) {
                return row.description || '-';
              },
            },
            {
              columnName: 'action',
              thClassName: 'text-center',
              tdClassName: 'text-center',
              headerText: t(`${prefix}.table.headers.action`),
              cellFormat({ index }) {
                if (readOnly) {
                  return '';
                }

                return (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      remove(index);
                      setDisabledEditButton(false);
                    }}
                  >
                    <Icon name="trash" />
                  </button>
                );
              },
            },
          ]}
          data={requirementRows}
          noResultsText={
            <div className="d-flex justify-content-center align-items-center py-5">
              <DisplayInfo
                iconName="information"
                title={t(`${prefix}.table.noResults.title`)}
                textBody={t(`${prefix}.table.noResults.description`)}
                maxWidth="650px"
              />
            </div>
          }
        />
      </Col>
      <AddAdmissionRequirementModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        options={pendingOptions}
        onAdd={(requirement) => {
          append(requirement);
          setDisabledEditButton(false);
        }}
      />
    </Row>
  );
};
